import { useEffect, useState } from "react"
import {
  useTelemedicineForPediatricsActivationStatus,
  useCreateTelemedicineForPediatricsEligibility,
  useInvalidateTelemedicineForPediatricsEligibility
} from "#/components/telemedicine/pediatrics/hooks/useTelemedicineForPediatricsActivation"

export enum AccessBlueberryAccountDialogStep {
  WHICH_EMAIL,
  LEAVING_CARROT,
  ERROR
}

const BLUEBERRY_APP_URL = "https://app.blueberrypediatrics.com/"

export interface UseAccessBlueberryAccountDialogState {
  step: AccessBlueberryAccountDialogStep
  email: string
  isLoading: boolean
  isError: boolean
  continueUrl: string | null
  isCreatingEligibility: boolean
  goToLeavingCarrot: (email: string) => void
  goToWhichEmail: () => void
  closeAndReset: () => void
}

export function useAccessBlueberryAccountDialog(onClose: () => void): UseAccessBlueberryAccountDialogState {
  const { activationStatus, enrollmentUrl, isLoading, isError } = useTelemedicineForPediatricsActivationStatus()
  const invalidateEligibility = useInvalidateTelemedicineForPediatricsEligibility()
  const { mutate: createEligibility, isLoading: isCreatingEligibility } =
    useCreateTelemedicineForPediatricsEligibility()

  const [step, setStep] = useState<AccessBlueberryAccountDialogStep>(AccessBlueberryAccountDialogStep.WHICH_EMAIL)
  const [email, setEmail] = useState<string>("")
  const [createdEnrollmentUrl, setCreatedEnrollmentUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoading && activationStatus !== "not-started") {
      setStep(AccessBlueberryAccountDialogStep.LEAVING_CARROT)
    }
  }, [isLoading, activationStatus])

  const getContinueUrl = (): string | null => {
    if (activationStatus === "activated") {
      return BLUEBERRY_APP_URL
    }
    return enrollmentUrl ?? createdEnrollmentUrl
  }

  return {
    step,
    email,
    isLoading,
    isError,
    continueUrl: getContinueUrl(),
    isCreatingEligibility,
    goToLeavingCarrot: (submittedEmail: string) => {
      setEmail(submittedEmail)
      setStep(AccessBlueberryAccountDialogStep.LEAVING_CARROT)
      if (activationStatus === "not-started") {
        createEligibility(submittedEmail, {
          onSuccess: (data) => {
            if (!data.eligibilityRecordId) {
              setStep(AccessBlueberryAccountDialogStep.ERROR)
              return
            }
            setCreatedEnrollmentUrl(data.enrollmentUrl)
          },
          onError: () => {
            setStep(AccessBlueberryAccountDialogStep.ERROR)
          }
        })
      }
    },
    goToWhichEmail: () => setStep(AccessBlueberryAccountDialogStep.WHICH_EMAIL),
    closeAndReset: () => {
      setStep(
        activationStatus !== "not-started"
          ? AccessBlueberryAccountDialogStep.LEAVING_CARROT
          : AccessBlueberryAccountDialogStep.WHICH_EMAIL
      )
      setEmail("")
      setCreatedEnrollmentUrl(null)
      invalidateEligibility()
      onClose()
    }
  }
}
